import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default English translations (fallback)
const defaultTranslations = {
  dashboard: "Dashboard",
  logs: "Logs",
  simulation: "Simulation",
  general_settings: "General Settings",
  views: "Views",
  navigation: "Navigation",
  concierge: "Concierge",
  family: "Family",
  doctor: "Doctor",
  medical_staff: "Medical Staff",
  residence_overview: "Residence Overview",
  apartments: "Apartments",
  devices_online: "devices online",
  alerts: "alerts",
  refresh: "Refresh",
  last_updated: "Last updated",
  activity: "Activity",
  bracelet: "Bracelet",
  temp: "Temp",
  humidity: "Humidity",
  co2: "CO2",
  breakfast_ok: "Breakfast OK",
  no_breakfast: "No breakfast",
  outside: "Outside",
  view: "View",
  acknowledge: "Acknowledge",
  resolve: "Resolve",
  resident_ok: "Resident OK",
  false_alarm: "False Alarm",
  real_incident: "Real Incident",
  settings: "Settings",
  how_it_works: "How It Works",
  save_changes: "Save Changes",
  ok: "OK",
  warning: "WARNING",
  urgent: "URGENT"
};

const TranslationContext = createContext({
  t: (key) => key,
  language: 'EN',
  setLanguage: () => {},
  isLoading: false
});

export function TranslationProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    return localStorage.getItem('eldera_language') || 'EN';
  });
  const [translations, setTranslations] = useState(defaultTranslations);
  const [isLoading, setIsLoading] = useState(false);

  const fetchTranslations = useCallback(async (lang) => {
    try {
      setIsLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/translations/${lang}`);
      if (response.ok) {
        const data = await response.json();
        setTranslations(data);
      }
    } catch (error) {
      console.error('Failed to fetch translations:', error);
      setTranslations(defaultTranslations);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTranslations(language);
  }, [language, fetchTranslations]);

  const setLanguage = useCallback((lang) => {
    localStorage.setItem('eldera_language', lang);
    setLanguageState(lang);
  }, []);

  const t = useCallback((key) => {
    return translations[key] || key;
  }, [translations]);

  return (
    <TranslationContext.Provider value={{ t, language, setLanguage, isLoading }}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  return useContext(TranslationContext);
}
